import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { api, type Me } from '../api';
import { money } from '../form';
import { errorText, useI18n } from '../i18n';
import { Alert } from '../layout';

interface Receivables {
  total: number; overdue: number;
  aging: { from: number; to: number | null; total: number }[];
  customers: { key: string; customerId: number | null; name: string; code: string | null; count: number; outstanding: number; overdue: number; maxDaysOverdue: number }[];
  invoices: { id: number; series: string; number: string; issueDate: string; dueDate: string | null; buyerName: string; total: number; currency: string; outstanding: number; daysOverdue: number; buyerEmail: string | null; lastReminderAt: string | null }[];
}


/** Kas man skolingas: unpaid issued invoices (credit invoices deducted), per customer, by age. */
const STEP_CHOICES = [1, 3, 7, 14, 30, 60];

/** Automatic reminders: after which days past the due date (up to three) and with what text; the owner decides. */
export function ReminderControl({ me }: { me: Me }) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const settings = useQuery({ queryKey: ['reminders'], queryFn: () => api.get<{ days: number[]; message: string | null; defaultMessage: string; maxOverdueDays: number }>('api/v1/reminders') });
  const [draft, setDraft] = useState<{ days: number[]; message: string } | null>(null);
  const dialog = useRef<HTMLDialogElement>(null);
  const save = useMutation({
    mutationFn: (body: { days: number[]; message: string }) => api.put('api/v1/reminders', body),
    onSuccess: async () => { setDraft(null); dialog.current?.close(); await queryClient.invalidateQueries({ queryKey: ['reminders'] }); },
  });
  const s = settings.data;
  if (!s) return null;
  const owner = me.company.role === 'owner' && !me.viewAsAdmin;
  const d = draft ?? { days: s.days, message: s.message ?? '' };
  const toggle = (day: number) => setDraft({ ...d, days: d.days.includes(day) ? d.days.filter((x) => x !== day) : [...d.days, day].sort((a, b) => a - b) });
  return (
    <>
      <button type="button" className={`button button--small ${s.days.length ? 'button--ghost is-on' : 'button--ghost'} reminder-toggle`} onClick={() => dialog.current?.showModal()}>
        {t('Priminimai')}: {s.days.length ? `${s.days.join(', ')} ${t('d.')}` : t('Išjungta')}
      </button>
      <dialog ref={dialog} className="confirm-dialog reminder-dialog" onClose={() => setDraft(null)}>
        <div className="dialog-card reminder-settings">
          <p className="eyebrow">{t('Priminimai')}</p><h2>{t('Automatiniai priminimai klientams')}</h2>
          <p className="muted">{t('Vėluojančios sąskaitos klientui automatiškai siunčiamas priminimas su PDF iš projekto el. pašto, darbo valandomis (9–17 val.). Kiekvienas žingsnis siunčiamas vieną kartą; sąskaitoms, vėluojančioms daugiau kaip {n} d., automatiškai nesiunčiama.', { n: s.maxOverdueDays })}</p>
          {save.isError ? <Alert kind="error">{errorText(t, save.error)}</Alert> : null}
          <fieldset className="plain-fieldset" disabled={!owner || save.isPending}>
            <p className="reminder-label">{t('Siųsti praėjus dienų po apmokėjimo termino (iki 3):')}</p>
            <div className="reminder-days">
              {STEP_CHOICES.map((day) => (
                <label key={day} className={`reminder-day${d.days.includes(day) ? ' is-on' : ''}`}>
                  <input type="checkbox" checked={d.days.includes(day)} disabled={!d.days.includes(day) && d.days.length >= 3} onChange={() => toggle(day)} />{day} {t('d.')}
                </label>
              ))}
            </div>
            <label>{t('Laiško tekstas')}<textarea rows={7} value={d.message} placeholder={s.defaultMessage} onChange={(e) => setDraft({ ...d, message: e.target.value })} /></label>
            <p className="muted reminder-hint">{t('Galite naudoti: {a} — numeris, {b} — skolos suma, {c} — terminas, {e} — vėlavimo dienos, {f} — siuntėjas. Tuščias laukas — standartinis tekstas.', { a: '{numeris}', b: '{suma}', c: '{terminas}', e: '{dienos}', f: '{siuntėjas}' })}</p>
            {!owner ? <p className="muted">{t('Nustatymus keičia įmonės savininkas.')}</p> : null}
          </fieldset>
          <div className="dialog-actions">
            <button type="button" className="button button--ghost" onClick={() => dialog.current?.close()}>{t('Uždaryti')}</button>
            {owner ? <button type="button" className="button button--primary" disabled={!draft || save.isPending} onClick={() => save.mutate(d)}>{t('Išsaugoti')}</button> : null}
          </div>
        </div>
      </dialog>
    </>
  );
}

interface MonthReport {
  month: string;
  issued: { count: number; net: number; vat: number; total: number };
  paid: { count: number; total: number };
  unpaid: number;
  received: { count: number; total: number; vat: number };
  projects: { projectId: number | null; name: string | null; count: number; total: number }[];
}

/** Mėnesio suvestinė on Apžvalga: pick a month; issued, paid, unpaid, VAT and received. */
export function MonthSummary({ vatPayer }: { vatPayer: boolean }) {
  const { t } = useI18n();
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const data = useQuery({ queryKey: ['month-report', month], queryFn: () => api.get<MonthReport>(`api/v1/reports/month?month=${month}`) });
  const m = data.data;
  return (
    <section className="panel-card">
      <div className="card-head">
        <div><p className="eyebrow">{t('Mėnesio suvestinė')}</p><h2>{new Date(`${month}-01T00:00:00`).toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}</h2></div>
        <input type="month" className="month-input" value={month} max={new Date().toISOString().slice(0, 7)} onChange={(e) => { if (e.target.value) setMonth(e.target.value); }} aria-label={t('Mėnuo')} />
      </div>
      {data.isError ? <Alert kind="error">{errorText(t, data.error)}</Alert> : null}
      <div className="month-grid">
        <div><span>{t('Išrašyta')}</span><strong>{money(m?.issued.total ?? 0)}</strong><small>{m?.issued.count ?? 0} {t('sąsk.')}{vatPayer ? ` · ${t('PVM')} ${money(m?.issued.vat ?? 0)}` : ''}</small></div>
        <div><span>{t('Apmokėta')}</span><strong>{money(m?.paid.total ?? 0)}</strong><small>{m?.paid.count ?? 0} {t('sąsk.')}</small></div>
        <div><span>{t('Neapmokėta')}</span><strong>{money(m?.unpaid ?? 0)}</strong><small>{t('iš šio mėnesio sąskaitų')}</small></div>
        <div><span>{t('Gautos sąskaitos')}</span><strong>{money(m?.received.total ?? 0)}</strong><small>{m?.received.count ?? 0} {t('sąsk.')}{vatPayer ? ` · ${t('PVM')} ${money(m?.received.vat ?? 0)}` : ''}</small></div>
      </div>
      {vatPayer ? <IsafDownload month={month} /> : null}
      {m && m.projects.length > 1 ? (
        <div className="month-projects">{m.projects.map((p) => (
          <div key={p.projectId ?? 0}><span>{p.name ?? t('Be projekto')}</span><small>{p.count} {t('sąsk.')}</small><strong>{money(p.total)}</strong></div>
        ))}</div>
      ) : null}
    </section>
  );
}

/** i.SAF for the chosen month (VAT payers). Invoices whose VAT code cannot be derived are listed instead. */
export function IsafDownload({ month }: { month: string }) {
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  const [problems, setProblems] = useState<{ invoice: string; reason: string; code?: string }[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const download = async () => {
    setBusy(true); setProblems(null); setError(null);
    try {
      const r = await fetch(`api/v1/export/isaf.xml?month=${month}`, { credentials: 'same-origin' });
      if (r.status === 422) { setProblems(((await r.json()) as { problems: { invoice: string; reason: string; code?: string }[] }).problems); return; }
      if (!r.ok) { const code = ((await r.json().catch(() => null)) as { error?: { code?: string } } | null)?.error?.code; setError(code === 'company_code_missing' ? t('Įmonės duomenyse nurodykite įmonės kodą.') : t('i.SAF failo sukurti nepavyko.')); return; }
      const url = URL.createObjectURL(await r.blob());
      const a = document.createElement('a');
      a.href = url;
      a.download = /filename="([^"]+)"/.exec(r.headers.get('content-disposition') ?? '')?.[1] ?? `isaf_${month}.xml`;
      a.click();
      URL.revokeObjectURL(url);
    } finally { setBusy(false); }
  };
  return (
    <div className="isaf-box">
      <button type="button" className="button button--ghost button--small" disabled={busy} onClick={() => void download()}>{busy ? t('Ruošiama…') : t('Atsisiųsti i.SAF (XML)')}</button>
      <small className="muted">{t('PVM sąskaitų faktūrų registras VMI: išrašytos ir gautos sąskaitos.')}</small>
      {error ? <Alert kind="error">{error}</Alert> : null}
      {problems ? (
        <Alert kind="error">
          {t('Šioms sąskaitoms reikia PVM klasifikacijos (0 %, neapmokestinama, eksportas, ES, atvirkštinis apmokestinimas). Atidarykite sąskaitą ir pasirinkite klasifikaciją — sumos nesikeičia:')}
          <ul>{problems.map((p) => <li key={p.invoice}>{p.invoice} — {p.reason === 'no_vat' ? t('be PVM') : p.reason === 'wrong_register' ? t('kodas {code} šiam registrui netinka', { code: p.code ?? '' }) : t('pasirinkite PVM klasifikaciją 0 % eilutėms')}</li>)}</ul>
        </Alert>
      ) : null}
    </div>
  );
}
